import { getSessionStorage } from 'config/appConfig'
import React from 'react'
import { customerContainer, mysiteContainer } from 'src/models'
import {
  currencyFormat,
  i18nTranslate,
  enablePlpRedesign,
  isINMarket,
} from 'src/utils'
import { MEMBER_PRICE } from 'src/utils/constant'
import {
  isEquinoxMysite,
  isEquinoxMysiteAdmin,
  isEquinoxMysiteAdminPreview,
  isEquinoxPOAdmin,
  isEquinoxPOSite,
} from 'src/utils/mySiteUtils'

/**
 * Renders the price component for a product card.
 *
 * This component calculates the unit price for a product based on its type (bundle, kit, or other).
 * If the product is a bundle or kit, the unit price is set to the total value of the product.
 * Otherwise, the unit price is set to the total value of the first SKU of the product.
 *
 * It handles cases where the price is discounted, has a negative discount, or is zero.
 * The component then renders the price information, including the original price, discounted price (if applicable), and a strikethrough for the original price (if discounted).
 *
 */

class ProductCardPrice extends React.Component {
  getMemberPricing = (product = {}) => {
    // For India market, member price is sourced from the "Member Price"
    // facet node instead of the "Wholesale Price" node.
    const priceFacetPattern = isINMarket() ? 'Member' : 'Wholesale'
    const memberPriceNode =
      product?.totalValue?.priceFacets?.[`${priceFacetPattern} Price`]
    const memberPrice = memberPriceNode?.[`${priceFacetPattern} Price`]
    const memberPriceAfterDiscountNode =
      memberPriceNode?.[`${priceFacetPattern} PriceAfterDiscount`]
    const regularPriceNode = product?.totalValue?.priceFacets?.['Regular Price']

    if (memberPriceNode) {
      if (memberPriceAfterDiscountNode) {
        if (
          memberPrice === memberPriceAfterDiscountNode ||
          memberPriceAfterDiscountNode > memberPrice
        ) {
          return {
            originalPrice: regularPriceNode?.['Regular Price'],
            priceAfterDiscount: memberPriceAfterDiscountNode,
          }
        } else {
          return {
            originalPrice: customerContainer?.isRegisterUser
              ? memberPrice
              : regularPriceNode?.['Regular Price'],
            priceAfterDiscount: memberPriceAfterDiscountNode,
          }
        }
      } else {
        return {
          originalPrice: regularPriceNode?.['Regular Price'],
          priceAfterDiscount: memberPrice,
        }
      }
    }
  }

  getRetailPricing = (product = {}) => {
    const retailPriceNode = product?.totalValue?.priceFacets?.['Regular Price']
    const retailPrice = retailPriceNode?.['Regular Price']
    const retailPriceAfterDiscountNode =
      retailPriceNode?.['Regular PriceAfterDiscount']

    if (retailPriceNode) {
      if (retailPriceAfterDiscountNode) {
        if (
          retailPrice === retailPriceAfterDiscountNode ||
          retailPriceAfterDiscountNode > retailPrice
        ) {
          return {
            originalPrice: retailPriceAfterDiscountNode,
            priceAfterDiscount: retailPriceAfterDiscountNode,
          }
        } else {
          return {
            originalPrice: retailPrice,
            priceAfterDiscount: retailPriceAfterDiscountNode,
          }
        }
      } else {
        return {
          originalPrice: retailPriceNode?.['Regular Price'],
          priceAfterDiscount: retailPriceNode?.['Regular Price'],
        }
      }
    }
  }

  getEquinoxMysitePrice = (unitPrice, product) => {
    let priceType = null
    if (isEquinoxMysiteAdmin() || isEquinoxPOAdmin()) {
      priceType = this.props.adminPriceType
    } else if (isEquinoxMysiteAdminPreview()) {
      priceType = mysiteContainer.mysitePriceType
    }
    if (!customerContainer?.isRegisterUser) {
      if (isEquinoxMysite() && mysiteContainer.mysitePriceType === MEMBER_PRICE) {
        return this.getMemberPricing(product)
      }
      if (isEquinoxPOSite()) {
        const personalOfferDetails = getSessionStorage('personalOffer')
        if (personalOfferDetails?.priceType === MEMBER_PRICE) {
          return this.getMemberPricing(product)
        }
      }
    } else if (customerContainer?.isRegisterUser) {
      if (
        (isEquinoxMysiteAdmin() ||
          isEquinoxMysiteAdminPreview() ||
          isEquinoxPOAdmin()) &&
        priceType
      ) {
        if (priceType?.toLowerCase() == 'member') {
          return this.getMemberPricing(product)
        } else if (priceType?.toLowerCase() == 'retail') {
          return this.getRetailPricing(product)
        }
      }
    }
    return unitPrice
  }

  getProductPrice = () => {
    const {
      product = {},
      productType,
      selectedPlpSku = {},
      bundlePriceOverride = {},
    } = this.props
    let unitPrice = {}
    if (productType === 'bundle' || productType === 'kit') {
      const hasBundlePriceOverride = Object.keys(bundlePriceOverride || {}).length > 0
      const modifiedProduct = hasBundlePriceOverride
        ? { ...product, totalValue: bundlePriceOverride }
        : product
      unitPrice =
        modifiedProduct?.totalValue || modifiedProduct?.prices?.[0] // fallback value if totalValue node is missing in product for bundle and kit
      unitPrice = this.getEquinoxMysitePrice(unitPrice, modifiedProduct)
    } else {
      unitPrice =
        enablePlpRedesign() &&
        (selectedPlpSku?.totalValue || selectedPlpSku?.prices)
          ? selectedPlpSku?.totalValue || selectedPlpSku?.prices?.[0]
          : product?.sku?.[0]?.totalValue || product?.sku?.[0]?.prices?.[0] // fallback value if totalValue node is missing in sku for regular product
      const productSku =
        enablePlpRedesign() && selectedPlpSku?.totalValue
          ? selectedPlpSku
          : product?.sku?.[0]
      unitPrice = this.getEquinoxMysitePrice(unitPrice, productSku)
    }
    return unitPrice || {}
  }
  renderPrice = () => {
    const { priceLable = false, currency: currencyProp } = this.props
    const currency = isEquinoxPOAdmin() ? currencyProp : undefined
    const {
      originalPrice,
      priceAfterDiscount,
      price = '',
    } = this.getProductPrice() || {}
    const isNegetiveDiscount = priceAfterDiscount > originalPrice
    const isDiscountedPrice = priceAfterDiscount < originalPrice
    const isZeroDiscount = priceAfterDiscount == 0

    if (isNegetiveDiscount) {
      return (
        <span className="negetive-discount" data-testid="qa-product-sale-price">
          {currencyFormat({ currency, price: priceAfterDiscount || 0.0 })}
        </span>
      )
    } else if (isZeroDiscount) {
      return (
        <span className="zero-discount" data-testid="qa-product-sale-price">
          {currencyFormat({ currency, price: originalPrice || 0.0 })}
        </span>
      )
    } else if (isDiscountedPrice) {
      return (
        <div className="discounted-price">
          <span
            className="discounted-price"
            data-testid="qa-product-sale-price">
            {currencyFormat({ currency, price: priceAfterDiscount || 0.0 })}
          </span>
          <span
            className={`strikeThrough  ${
              priceLable ? 'stike-price-value' : 'pr-2'
            }`}
            data-testid="qa-product-reg-price">
            {currencyFormat({ currency, price: originalPrice || 0.0 })}
          </span>
        </div>
      )
    } else {
      return (
        <span className="reguler-price" data-testid="qa-product-sale-price">
          {currencyFormat({ currency, price: priceAfterDiscount || price || 0.0 })}
        </span>
      )
    }
  }
  render() {
    const { priceLable = false } = this.props

    return (
      <div className="product-card-price">
        {priceLable && (
          <div>
            {i18nTranslate('plp.price', 'Price', {
              nameSpace: 'ssr-resource',
            })}
            <span className="pr-1">:</span>
          </div>
        )}
        {this.renderPrice()}
      </div>
    )
  }
}

export { ProductCardPrice }
export default ProductCardPrice
